import * as React from "react";
import {
  Card, CardActionArea,
  CardContent, CardMedia,
  createStyles,
  Theme,
  Typography,
  withStyles,
  WithStyles
} from "@material-ui/core";
import {inject, observer} from "mobx-react";
import Class from "../../model/Class";
import S3UrlCacheStore from "../../stores/S3UrlCacheStore";
import {observable} from "mobx";
import TitleButton from "../../components/TitleButton";
import {format, isBefore, startOfToday} from "date-fns";
import {isoToLocalDate} from "../../stores/StoreUtilities";
import Visible from "../../components/Visible";
import AccountStore from "../../stores/AccountStore";
import UserStore from "../../stores/UserStore";

const styles = (theme: Theme) => createStyles({
  card: {
    width: '100%',
    minHeight: 90,
    maxHeight: 90,
    padding: 0
  },
  content: {
    display: "flex",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    padding: 0,
  },
  left: {
    flexShrink: 0,
    // padding: "8px 0 3px 8px",
    padding: 0,
    alignSelf: "top",
  },
  middle: {
    flexGrow: 1,
    padding: "5px 8px 0 8px",
  },
  right: {
    flexShrink: 0,
    alignSelf: "flex-start",
    padding: "5px 8px 0 0"
  },
  actionArea: {
    color: theme.palette.secondary.main
  },
  title: {
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.2,
    paddingBottom: 4,
    // maxHeight: 26,
    // overflow: "hidden",
  },
  titleButtons: {
    textAlign: "right"
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.3,
    paddingBottom: 4
  },
  titleDescriptionMax: {
    maxHeight: 62,
    overflow: "hidden",
  },
  titleDescription: {
    maxHeight: 76,
    overflow: "hidden",
  },
  seats: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 400,
    // maxHeight: 24,
    lineHeight: 1.3,
    textAlign: "center",
    paddingLeft: 5
  },
  owner: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 400,
    // maxHeight: 24,
    lineHeight: 1.3,
    textAlign: "center",
    paddingLeft: 5,
    paddingTop: 4,
  },
  details: {
    fontSize: 14,
    fontWeight: 400,
    maxHeight: 24,
    overflow: "hidden",
  },
  cover: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  },
  mediaArea: {
    display: "flex",
    justifyContent: "center",
  },
  media: {
    width: 90,
    height: 90
  },
  image: {
    width: "100%",
    height: "auto"
  }
})

interface IClassCardProps {
  classObj: Class
  s3UrlCacheStore?: S3UrlCacheStore
  userStore?: UserStore
  accountStore?: AccountStore
  onCardAction?: any
  onAddClass?: any
}

@inject("s3UrlCacheStore", "userStore", "accountStore")
@observer
class ClassCard extends React.Component<WithStyles<typeof styles> & IClassCardProps> {

  @observable coverImageUrl?: string

  componentDidMount() {
    const { s3UrlCacheStore, classObj } = this.props

    if (classObj.course && classObj.course.coverUrl) {
      const coverUrl = classObj.course.coverUrl
      s3UrlCacheStore!.get(coverUrl)
        .then((url: string | undefined) => {
          this.coverImageUrl = url
        })
        .catch((err: Error) => {
          console.log("Error loading cover image")
        })
    }
  }

  render() {
    const { classes, classObj, onCardAction, onAddClass, userStore, accountStore } = this.props

    let title = ""
    let description = ""
    let term = ""
    let seats = ""
    let isEnded = false
    let isStudent = userStore!.isStudent || accountStore!.isIndividual

    if (classObj.course) {
      const course = classObj.course
      title = course.title
      description = course.description
      if (course.creditHours) {
        description += ` (${course.creditHours} hours)`
      }
      if (classObj.termBegin && classObj.termEnd) {
        const termBegin = isoToLocalDate(classObj.termBegin)
        const termEnd = isoToLocalDate(classObj.termEnd)
        term = `${format(termBegin, 'M/d/yy')} - ${format(termEnd, 'M/d/yy')}`
        isEnded = (isBefore(termEnd, startOfToday()))
      }

      if (classObj.seatsFilled !== undefined && classObj.seatsFilled !== null) {
        seats = classObj.seatsFilled.toString()
        if (classObj.seatsPurchased && !course.isFree) {
          seats += `/${classObj.seatsPurchased}`
        }
      }
    }

    return (
      <Card className={classes.card}>
          <CardContent className={classes.content}>
            <div className={classes.left}>
              <CardActionArea onClick={this.onCardAction}>
                <CardMedia className={classes.media}
                           image={this.coverImageUrl}
                           title={title}/>
              </CardActionArea>
            </div>
            <div className={classes.middle}>
              <div className={!classObj.id ? classes.titleDescription : classes.titleDescriptionMax}>
                <CardActionArea onClick={this.onCardAction} className={classes.actionArea}>
                  <Typography className={classes.title}>
                    {title}
                  </Typography>
                </CardActionArea>
                <Typography className={classes.description}>
                  {description}
                </Typography>
              </div>
              <Visible if={false /*term !== "" && !isStudent*/}>
                <Typography className={classes.description}>
                  {term}
                </Typography>
              </Visible>
            </div>
            <div className={classes.right}>
              {!classObj.id && onAddClass &&
                <Typography className={classes.titleButtons}>
                  <TitleButton title="+ ADD" variant="primary" onClick={this.onAddClass}/>
                </Typography>
              }
              {classObj.id && onCardAction && !isEnded && !isStudent &&
                <Typography className={classes.titleButtons}>
                  <TitleButton title="EDIT" variant="secondary" onClick={this.onEditClass}/>
                </Typography>
              }
              <Typography className={classes.seats}>
                &nbsp;
              </Typography>
              {classObj.id && !isStudent &&
                <Typography className={classes.seats}>
                  {seats.length > 0 && <span>{seats}<br/>seats</span>}
                </Typography>
              }
              {classObj.id && isStudent &&
                <Typography className={classes.owner}>
                  {classObj.userId ? "Self" : "Agency"}
                </Typography>
              }
            </div>
          </CardContent>
      </Card>
    )
  }

  onAddClass = () => {
    const { classObj, onAddClass } = this.props

    if (onAddClass) {
      onAddClass(classObj.courseId)
    }
  }

  onEditClass = () => {
    const { onCardAction, classObj } = this.props

    if (onCardAction) {
      onCardAction("edit", classObj)
    }
  }

  onCardAction = () => {
    const { onCardAction, classObj } = this.props

    if (onCardAction) {
      onCardAction("open", classObj)
    }
  }

}

export default withStyles(styles)(ClassCard)
